import { FormEvent, useRef, useState } from 'react';
import TextInput from '@components/application/input/textInput';
import Recaptcha from '@components/application/input/recaptcha';
import {
  EnquiryFormProps,
  TextInputInstance,
  RecaptchaInstance,
} from '@sharedTypes/EnquiryFormTypes';

const EnquiryForm: React.FC<EnquiryFormProps> = ({
  action,
  authenticityToken,
  collectionPoint,
  finance,
  interestedInFinance,
  isTest,
  recaptchaSiteKey,
  stockReference,
}: EnquiryFormProps) => {
  const [isValid, setIsValid] = useState(true);

  const firstNameRef = useRef<TextInputInstance | null>(null);
  const lastNameRef = useRef<TextInputInstance | null>(null);
  const postcodeRef = useRef<TextInputInstance | null>(null);
  const contactNumberRef = useRef<TextInputInstance | null>(null);
  const contactEmailRef = useRef<TextInputInstance | null>(null);
  const recaptchaRef = useRef<RecaptchaInstance | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    let formIsValid = true;

    if (!firstNameRef.current?.handleValidation()) {
      formIsValid = false;
    }

    if (!lastNameRef.current?.handleValidation()) {
      formIsValid = false;
    }

    if (!postcodeRef.current?.handleValidation()) {
      formIsValid = false;
    }

    if (!contactNumberRef.current?.handleValidation()) {
      formIsValid = false;
    }

    if (!contactEmailRef.current?.handleValidation()) {
      formIsValid = false;
    }

    if (!recaptchaRef.current?.validate()) {
      formIsValid = false;
    }

    if (!formIsValid) {
      event.preventDefault();
      setIsValid(false);
      return;
    }

    setIsValid(true);
  };

  const renderFinanceHiddenFields = () => {
    if (!finance) return null;

    return (
      <div>
        <input
          name="enquiry[enquiry_finance_attributes][description]"
          type="hidden"
          value={finance.description}
        />
        <input
          name="enquiry[enquiry_finance_attributes][calculated]"
          type="hidden"
          value={finance.calculated}
        />
        <input
          name="enquiry[enquiry_finance_attributes][term]"
          type="hidden"
          value={finance.term}
        />
        <input
          name="enquiry[enquiry_finance_attributes][deposit]"
          type="hidden"
          value={finance.deposit}
        />
        <input
          name="enquiry[enquiry_finance_attributes][annual_mileage]"
          type="hidden"
          value={finance.annual_mileage}
        />
        <input
          name="enquiry[enquiry_finance_attributes][monthly_payment]"
          type="hidden"
          value={finance.monthly_payment}
        />
        <input
          name="enquiry[enquiry_finance_attributes][guaranteed_future_value]"
          type="hidden"
          value={finance.guaranteed_future_value}
        />
        <input
          name="enquiry[enquiry_finance_attributes][completion_fee]"
          type="hidden"
          value={finance.completion_fee}
        />
        <input
          name="enquiry[enquiry_finance_attributes][fixed_interest_rate]"
          type="hidden"
          value={finance.fixed_interest_rate}
        />
        <input
          name="enquiry[enquiry_finance_attributes][apr]"
          type="hidden"
          value={finance.apr}
        />
        <input
          name="enquiry[enquiry_finance_attributes][excess_mileage_charge]"
          type="hidden"
          value={finance.excess_mileage_charge}
        />
      </div>
    );
  };

  const renderCollectionPoint = () => {
    if (!collectionPoint) return null;

    return (
      <input
        name="enquiry[collection_point]"
        type="hidden"
        value={collectionPoint}
      />
    );
  };

  const renderFormError = () => {
    if (isValid) return null;

    return (
      <p className="ac-enquiry-form__error-msg--show">
        Oops! Some of your information isn&apos;t quite right. Please correct
        the fields above.
      </p>
    );
  };

  return (
    <form
      acceptCharset="UTF-8"
      action={action}
      className="ch-form__group"
      id="new_enquiry"
      method="post"
      onSubmit={onSubmit}
    >
      <input name="utf8" type="hidden" value="✓" />
      <input
        name="authenticity_token"
        type="hidden"
        value={authenticityToken}
      />
      <input
        name="enquiry[stock_reference]"
        type="hidden"
        value={stockReference}
      />
      <input
        name="enquiry[interested_in_finance]"
        type="hidden"
        value={interestedInFinance.toString()}
      />
      <input name="enquiry[dealbuilder]" type="hidden" value="false" />
      {renderFinanceHiddenFields()}
      {renderCollectionPoint()}

      <fieldset>
        <legend className="ch-reader">Your name</legend>
        <TextInput
          additionalInputClasses="ch-text--capitalize"
          errorMessage="What is your first name? &#40;maximum of 30 characters&#41;"
          inputId="enquiry_first_name"
          inputName="enquiry[first_name]"
          labelName="First name"
          ref={(el) => (firstNameRef.current = el)}
          requiresNameValidation
        />
        <TextInput
          additionalInputClasses="ch-text--capitalize"
          errorMessage="What is your last name? &#40;maximum of 30 characters&#41;"
          inputId="enquiry_last_name"
          inputName="enquiry[last_name]"
          labelName="Last name"
          ref={(el) => (lastNameRef.current = el)}
          requiresNameValidation
        />
      </fieldset>

      <fieldset>
        <legend className="ch-reader">Your contact details</legend>
        <TextInput
          additionalInputClasses="ch-text--uppercase"
          description="This will help us find your nearest branch."
          errorMessage="Please enter a valid postcode"
          inputId="enquiry_postcode"
          inputName="enquiry[postcode]"
          labelName="Postcode"
          ref={(el) => (postcodeRef.current = el)}
          requiresPostcodeValidation
        />
        <TextInput
          description="We'll give you a call to discuss your enquiry."
          errorMessage="Please enter a valid phone number"
          inputId="enquiry_contact_number"
          inputName="enquiry[contact_number]"
          inputType="tel"
          labelName="Phone number"
          ref={(el) => (contactNumberRef.current = el)}
          requiresPhoneNumberValidation
        />
        <TextInput
          description="We'll send a confirmation email to this address. We won't spam you."
          errorMessage="Please enter a valid email address"
          inputId="enquiry_contact_email"
          inputName="enquiry[contact_email]"
          inputType="email"
          labelName="Email address"
          ref={(el) => (contactEmailRef.current = el)}
          requiresEmailValidation
        />
      </fieldset>

      <fieldset>
        <legend className="ch-reader">Comments</legend>
        <label className="ch-label" htmlFor="enquiry_comments">
          Your message &#40;optional&#41;
        </label>
        <span className="ch-form__control-hint">
          If you have a specific question or comment, let us know.
        </span>
        <textarea
          className="ch-form__control"
          id="enquiry_comments"
          name="enquiry[comments]"
          rows={4}
        />
      </fieldset>

      <fieldset className="md:ch-mt--2">
        <legend className="ch-reader">Your newsletter preferences</legend>
        <div className="ch-form__group">
          <input
            className="ch-checkbox"
            id="newsletter"
            name="enquiry[newsletter]"
            type="checkbox"
          />
          <label
            className="ch-checkbox__label ch-fs--2 ch-fw--500 ch-color--ac-black"
            htmlFor="newsletter"
          >
            Subscribe to our newsletter for exclusive offers
          </label>
        </div>

        <Recaptcha
          className="g-recaptcha"
          errorMessage="Please complete the reCAPTCHA."
          isTest={isTest}
          recaptchaSiteKey={recaptchaSiteKey}
          ref={(el) => (recaptchaRef.current = el)}
        />

        {renderFormError()}
      </fieldset>
      <button
        className="ch-btn ch-width--12 ch-btn--success"
        name="button"
        type="submit"
      >
        Send enquiry
      </button>
    </form>
  );
};

export default EnquiryForm;
